type NodeId = string

export type NodeConnections = {
  children: Set<NodeId>
  parents: Set<NodeId>
  descendants: Set<NodeId>
  ancestors: Set<NodeId>
}

export type NodeHierarchyMap = Map<NodeId, NodeConnections>

type ChildMap = Map<NodeId, Set<NodeId>>

type IndexedGraph = {
  nodeIds: NodeId[]
  childrenByNodeIndex: number[][]
  parentsByNodeIndex: Array<Set<NodeId>>
  hasSelfLoopByNodeIndex: boolean[]
}

type Component = {
  nodeIndexes: number[]
  isCyclical: boolean
}

type Components = {
  components: Component[]
  componentByNodeIndex: Int32Array
}

/**
 * This is a hyper-optimized AI mess.
 * It also works, and is fast - which turns up to be crucial with large bots (~4000+ dialogs)
 *
 * If it works, it works.
 */
export function createNodeHierarchyMap(childMap: ChildMap): NodeHierarchyMap {
  const graph = createIndexedGraph(childMap)
  const { components, componentByNodeIndex } = findStronglyConnectedComponents(graph)

  if (components.length === 1) {
    return createSingleComponentHierarchyMap(childMap, graph, components[0])
  }

  const { componentChildren, componentParents } = createComponentConnections(
    graph.childrenByNodeIndex,
    componentByNodeIndex,
    components.length
  )
  const descendantsByComponent = createReachableNodeSets(
    components,
    computeDescendantComponentBitSets(componentChildren),
    graph.nodeIds
  )
  const ancestorsByComponent = createReachableNodeSets(
    components,
    computeAncestorComponentBitSets(componentParents),
    graph.nodeIds
  )

  return createHierarchyMap(
    childMap,
    graph,
    components,
    componentByNodeIndex,
    descendantsByComponent,
    ancestorsByComponent
  )
}

function createIndexedGraph(childMap: ChildMap): IndexedGraph {
  const nodeIds: NodeId[] = []
  const nodeIndex = new Map<NodeId, number>()
  const childrenByNodeIndex: number[][] = []
  const parentsByNodeIndex: Array<Set<NodeId>> = []
  const hasSelfLoopByNodeIndex: boolean[] = []

  const getNodeIndex = (nodeId: NodeId): number => {
    const existingIndex = nodeIndex.get(nodeId)

    if (existingIndex !== undefined) {
      return existingIndex
    }

    const newIndex = nodeIds.length

    nodeIds.push(nodeId)
    nodeIndex.set(nodeId, newIndex)
    childrenByNodeIndex.push([])
    parentsByNodeIndex.push(new Set())
    hasSelfLoopByNodeIndex.push(false)

    return newIndex
  }

  for (const nodeId of childMap.keys()) {
    getNodeIndex(nodeId)
  }

  for (const [parentId, children] of childMap.entries()) {
    const parentIndex = getNodeIndex(parentId)
    const childIndexes: number[] = []

    childrenByNodeIndex[parentIndex] = childIndexes

    for (const childId of children) {
      const childIndex = getNodeIndex(childId)

      childIndexes.push(childIndex)
      parentsByNodeIndex[childIndex].add(parentId)

      if (childIndex === parentIndex) {
        hasSelfLoopByNodeIndex[parentIndex] = true
      }
    }
  }

  return {
    nodeIds,
    childrenByNodeIndex,
    parentsByNodeIndex,
    hasSelfLoopByNodeIndex
  }
}

function findStronglyConnectedComponents(graph: IndexedGraph): Components {
  const nodeCount = graph.nodeIds.length
  const indexByNodeIndex = new Int32Array(nodeCount)
  const lowLinkByNodeIndex = new Int32Array(nodeCount)
  const componentByNodeIndex = new Int32Array(nodeCount)
  const isOnStackByNodeIndex = new Uint8Array(nodeCount)
  const tarjanStack: number[] = []
  const callStack: Array<{ nodeIndex: number; nextChildOffset: number }> = []
  const components: Component[] = []
  let nextIndex = 0

  indexByNodeIndex.fill(-1)
  componentByNodeIndex.fill(-1)

  const visitNode = (nodeIndex: number): void => {
    indexByNodeIndex[nodeIndex] = nextIndex
    lowLinkByNodeIndex[nodeIndex] = nextIndex
    nextIndex++

    tarjanStack.push(nodeIndex)
    isOnStackByNodeIndex[nodeIndex] = 1
    callStack.push({ nodeIndex, nextChildOffset: 0 })
  }

  for (let startNodeIndex = 0; startNodeIndex < nodeCount; startNodeIndex++) {
    if (indexByNodeIndex[startNodeIndex] !== -1) {
      continue
    }

    visitNode(startNodeIndex)

    while (callStack.length > 0) {
      const frame = callStack[callStack.length - 1]
      const children = graph.childrenByNodeIndex[frame.nodeIndex]

      if (frame.nextChildOffset < children.length) {
        const childIndex = children[frame.nextChildOffset]

        frame.nextChildOffset++

        if (indexByNodeIndex[childIndex] === -1) {
          visitNode(childIndex)
          continue
        }

        if (isOnStackByNodeIndex[childIndex] === 1) {
          lowLinkByNodeIndex[frame.nodeIndex] = Math.min(
            lowLinkByNodeIndex[frame.nodeIndex],
            indexByNodeIndex[childIndex]
          )
        }

        continue
      }

      const nodeIndex = frame.nodeIndex

      if (lowLinkByNodeIndex[nodeIndex] === indexByNodeIndex[nodeIndex]) {
        const componentIndex = components.length
        const nodeIndexes: number[] = []
        let componentNodeIndex: number

        do {
          componentNodeIndex = tarjanStack.pop()!
          isOnStackByNodeIndex[componentNodeIndex] = 0
          componentByNodeIndex[componentNodeIndex] = componentIndex
          nodeIndexes.push(componentNodeIndex)
        } while (componentNodeIndex !== nodeIndex)

        components.push({
          nodeIndexes,
          isCyclical: nodeIndexes.length > 1 || graph.hasSelfLoopByNodeIndex[nodeIndexes[0]]
        })
      }

      callStack.pop()

      if (callStack.length > 0) {
        const parentNodeIndex = callStack[callStack.length - 1].nodeIndex

        lowLinkByNodeIndex[parentNodeIndex] = Math.min(
          lowLinkByNodeIndex[parentNodeIndex],
          lowLinkByNodeIndex[nodeIndex]
        )
      }
    }
  }

  return { components, componentByNodeIndex }
}

function createSingleComponentHierarchyMap(
  childMap: ChildMap,
  graph: IndexedGraph,
  component: Component
): NodeHierarchyMap {
  const descendants = component.isCyclical
    ? createNodeSetFromIndexes(component.nodeIndexes, graph.nodeIds)
    : new Set<NodeId>()
  const ancestors = component.isCyclical
    ? createNodeSetFromIndexes(component.nodeIndexes, graph.nodeIds)
    : new Set<NodeId>()
  const hierarchyMap: NodeHierarchyMap = new Map()

  for (let nodeIndex = 0; nodeIndex < graph.nodeIds.length; nodeIndex++) {
    const nodeId = graph.nodeIds[nodeIndex]

    hierarchyMap.set(nodeId, {
      children: childMap.get(nodeId) ?? new Set(),
      parents: graph.parentsByNodeIndex[nodeIndex],
      descendants: createConnectionSet(descendants, component),
      ancestors: createConnectionSet(ancestors, component)
    })
  }

  return hierarchyMap
}

function createComponentConnections(
  childrenByNodeIndex: number[][],
  componentByNodeIndex: Int32Array,
  componentCount: number
): { componentChildren: Array<Set<number>>; componentParents: Array<Set<number>> } {
  const componentChildren = createComponentSetArray(componentCount)
  const componentParents = createComponentSetArray(componentCount)

  for (let nodeIndex = 0; nodeIndex < childrenByNodeIndex.length; nodeIndex++) {
    const parentComponent = componentByNodeIndex[nodeIndex]

    for (const childIndex of childrenByNodeIndex[nodeIndex]) {
      const childComponent = componentByNodeIndex[childIndex]

      if (parentComponent === childComponent) {
        continue
      }

      componentChildren[parentComponent].add(childComponent)
      componentParents[childComponent].add(parentComponent)
    }
  }

  return { componentChildren, componentParents }
}

function createComponentSetArray(componentCount: number): Array<Set<number>> {
  return Array.from({ length: componentCount }, () => new Set<number>())
}

function computeDescendantComponentBitSets(componentChildren: Array<Set<number>>): Uint32Array[] {
  const reachableComponentSets = createComponentBitSets(componentChildren.length)

  // Tarjan emits sink components first, so each child bitset is ready here.
  for (let componentIndex = 0; componentIndex < componentChildren.length; componentIndex++) {
    const reachableComponentSet = reachableComponentSets[componentIndex]

    for (const childComponent of componentChildren[componentIndex]) {
      addBit(reachableComponentSet, childComponent)
      orBitSetInto(reachableComponentSet, reachableComponentSets[childComponent])
    }
  }

  return reachableComponentSets
}

function computeAncestorComponentBitSets(componentParents: Array<Set<number>>): Uint32Array[] {
  const reachableComponentSets = createComponentBitSets(componentParents.length)

  // Parents are emitted after children, so walking backwards resolves ancestors in one pass.
  for (let componentIndex = componentParents.length - 1; componentIndex >= 0; componentIndex--) {
    const reachableComponentSet = reachableComponentSets[componentIndex]

    for (const parentComponent of componentParents[componentIndex]) {
      addBit(reachableComponentSet, parentComponent)
      orBitSetInto(reachableComponentSet, reachableComponentSets[parentComponent])
    }
  }

  return reachableComponentSets
}

function createComponentBitSets(componentCount: number): Uint32Array[] {
  const wordCount = Math.ceil(componentCount / 32)

  return Array.from({ length: componentCount }, () => new Uint32Array(wordCount))
}

function addBit(bitSet: Uint32Array, bitIndex: number): void {
  bitSet[bitIndex >> 5] |= 1 << (bitIndex & 31)
}

function orBitSetInto(target: Uint32Array, source: Uint32Array): void {
  for (let i = 0; i < target.length; i++) {
    target[i] |= source[i]
  }
}

function createReachableNodeSets(
  components: Component[],
  reachableComponentSets: Uint32Array[],
  nodeIds: NodeId[]
): Array<Set<NodeId>> {
  const nodeIdsByComponent = components.map(component =>
    component.nodeIndexes.map(nodeIndex => nodeIds[nodeIndex])
  )

  return reachableComponentSets.map((reachableComponentSet, componentIndex) => {
    const reachableNodes = new Set<NodeId>()

    if (components[componentIndex].isCyclical) {
      addNodes(reachableNodes, nodeIdsByComponent[componentIndex])
    }

    forEachSetBit(reachableComponentSet, reachableComponentIndex => {
      addNodes(reachableNodes, nodeIdsByComponent[reachableComponentIndex])
    })

    return reachableNodes
  })
}

function forEachSetBit(bitSet: Uint32Array, iteratee: (bitIndex: number) => void): void {
  for (let wordIndex = 0; wordIndex < bitSet.length; wordIndex++) {
    let word = bitSet[wordIndex]

    while (word !== 0) {
      const lowestSetBit = word & -word

      iteratee((wordIndex << 5) + 31 - Math.clz32(lowestSetBit))
      word ^= lowestSetBit
    }
  }
}

function createHierarchyMap(
  childMap: ChildMap,
  graph: IndexedGraph,
  components: Component[],
  componentByNodeIndex: Int32Array,
  descendantsByComponent: Array<Set<NodeId>>,
  ancestorsByComponent: Array<Set<NodeId>>
): NodeHierarchyMap {
  const hierarchyMap: NodeHierarchyMap = new Map()

  for (let nodeIndex = 0; nodeIndex < graph.nodeIds.length; nodeIndex++) {
    const nodeId = graph.nodeIds[nodeIndex]
    const componentIndex = componentByNodeIndex[nodeIndex]
    const component = components[componentIndex]

    hierarchyMap.set(nodeId, {
      children: childMap.get(nodeId) ?? new Set(),
      parents: graph.parentsByNodeIndex[nodeIndex],
      descendants: createConnectionSet(descendantsByComponent[componentIndex], component),
      ancestors: createConnectionSet(ancestorsByComponent[componentIndex], component)
    })
  }

  return hierarchyMap
}

function createConnectionSet(source: Set<NodeId>, component: Component): Set<NodeId> {
  if (component.nodeIndexes.length === 1) {
    return source
  }

  return createCopyOnWriteSet(source)
}

function createCopyOnWriteSet(source: Set<NodeId>): Set<NodeId> {
  return new CopyOnWriteSet(source)
}

class CopyOnWriteSet implements Set<NodeId> {
  readonly [Symbol.toStringTag] = 'Set'

  private backingSet: Set<NodeId>

  constructor(private readonly source: Set<NodeId>) {
    this.backingSet = source
  }

  get size(): number {
    return this.backingSet.size
  }

  add(nodeId: NodeId): this {
    if (!this.backingSet.has(nodeId)) {
      this.getMutableBackingSet().add(nodeId)
    }

    return this
  }

  clear(): void {
    if (this.backingSet.size > 0) {
      this.getMutableBackingSet().clear()
    }
  }

  delete(nodeId: NodeId): boolean {
    if (!this.backingSet.has(nodeId)) {
      return false
    }

    return this.getMutableBackingSet().delete(nodeId)
  }

  entries(): SetIterator<[NodeId, NodeId]> {
    return this.backingSet.entries()
  }

  forEach(
    callback: (value: NodeId, valueAgain: NodeId, set: Set<NodeId>) => void,
    thisArg?: unknown
  ): void {
    this.backingSet.forEach(nodeId => callback.call(thisArg, nodeId, nodeId, this))
  }

  has(nodeId: NodeId): boolean {
    return this.backingSet.has(nodeId)
  }

  union<U>(other: ReadonlySetLike<U>): Set<NodeId | U> {
    const result = new Set<NodeId | U>(this.backingSet)

    forEachSetLikeValue(other, value => result.add(value))

    return result
  }

  intersection<U>(other: ReadonlySetLike<U>): Set<NodeId & U> {
    const result = new Set<NodeId & U>()

    for (const nodeId of this.backingSet) {
      if (other.has(nodeId as unknown as U)) {
        result.add(nodeId as NodeId & U)
      }
    }

    return result
  }

  difference<U>(other: ReadonlySetLike<U>): Set<NodeId> {
    const result = new Set<NodeId>()

    for (const nodeId of this.backingSet) {
      if (!other.has(nodeId as unknown as U)) {
        result.add(nodeId)
      }
    }

    return result
  }

  symmetricDifference<U>(other: ReadonlySetLike<U>): Set<NodeId | U> {
    const result = new Set<NodeId | U>(this.backingSet)

    forEachSetLikeValue(other, value => {
      if (result.has(value)) {
        result.delete(value)
        return
      }

      result.add(value)
    })

    return result
  }

  isSubsetOf(other: ReadonlySetLike<unknown>): boolean {
    for (const nodeId of this.backingSet) {
      if (!other.has(nodeId)) {
        return false
      }
    }

    return true
  }

  isSupersetOf(other: ReadonlySetLike<unknown>): boolean {
    let result = true

    forEachSetLikeValue(other, value => {
      if (!this.backingSet.has(value as NodeId)) {
        result = false
      }
    })

    return result
  }

  isDisjointFrom(other: ReadonlySetLike<unknown>): boolean {
    let result = true

    forEachSetLikeValue(other, value => {
      if (this.backingSet.has(value as NodeId)) {
        result = false
      }
    })

    return result
  }

  keys(): SetIterator<NodeId> {
    return this.backingSet.keys()
  }

  values(): SetIterator<NodeId> {
    return this.backingSet.values()
  }

  [Symbol.iterator](): SetIterator<NodeId> {
    return this.values()
  }

  private getMutableBackingSet(): Set<NodeId> {
    if (this.backingSet === this.source) {
      this.backingSet = new Set(this.source)
    }

    return this.backingSet
  }
}

function forEachSetLikeValue<T>(setLike: ReadonlySetLike<T>, iteratee: (value: T) => void): void {
  const iterator = setLike.keys()
  let step = iterator.next()

  while (!step.done) {
    iteratee(step.value)
    step = iterator.next()
  }
}

function createNodeSetFromIndexes(nodeIndexes: number[], nodeIds: NodeId[]): Set<NodeId> {
  const result = new Set<NodeId>()

  for (const nodeIndex of nodeIndexes) {
    result.add(nodeIds[nodeIndex])
  }

  return result
}

function addNodes(target: Set<NodeId>, nodes: NodeId[]): void {
  for (const nodeId of nodes) {
    target.add(nodeId)
  }
}
